import { motion } from 'framer-motion';
import { Container, Typography, Grid, Box, Button, Paper } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { useEffect } from 'react';

const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(120deg, rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.15)), url("images/4677_better.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  position: 'relative',
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    minHeight: '80vh',
    paddingTop: theme.spacing(10),
  },
}));

const StatsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  textAlign: 'left',
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.06)',
  minHeight: '160px',
}));

const ServiceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  backgroundColor: '#FFFFFF',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const services = [
  {
    title: 'Development Services',
    description: 'Comprehensive development services with 30 years of successful project approvals, specializing in environmentally challenging areas.',
    features: [
      'Master land use planning, design and entitlement services',
      'Infrastructure and renewable energy design and engineering services',
      'Public and Private funding assistance and capital stack assembly',
      'Horizontal and Vertical Construction Management Services',
      'Owners Representative Services as Fiduciary to the Owner',
      'May participate as an equity partner and stay-in long term',
      'Master Development Agreement preparation and submittal packages',
      'Specializing in Environmentally challenging project areas'
    ]
  },
  {
    title: 'Brokerage Services',
    description: 'Over $3.5 Billion in Gross Closed Transactions across various property types with comprehensive sales and marketing strategies.',
    features: [
      'Permitting and Entitlement services included',
      'Customized Pre-Sales and Sales Launch strategies',
      'Luxury Single Family, Multi-Family, Commercial, Hotel and Residence Club sales',
      'Affiliate relationships with Airlines, Hotel Flags, and Residence Clubs',
      'Full market analysis and scoping for Sales Launch Strategy',
      'Sales team hiring, training, and management',
      'Sales velocity and closing schedule optimization'
    ]
  },
  {
    title: 'Consulting Services',
    description: 'Expert consulting services for sustainable resort and residence club development with comprehensive financial analysis.',
    features: [
      'Sustainable Resort and Residence Club Development Consulting',
      'Financial and Pro-Forma Analysis',
      'ROI and Capital Stack assistance',
      'Planning, permitting, and feasibility analysis',
      'Workout services for Banks and Lenders',
      'Cash Flow Modeling and Operational Planning',
      'Legal and Tax planning strategies',
      'Qualified Opportunity Zone Funding options'
    ]
  },
  {
    title: 'Project Management',
    description: 'Comprehensive project management services ensuring successful delivery of complex developments.',
    features: [
      'Full project lifecycle management',
      'Stakeholder coordination and communication',
      'Budget and timeline management',
      'Quality control and assurance',
      'Risk management and mitigation',
      'Contract administration',
      'Team leadership and coordination'
    ]
  }
];

export default function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ width: '100%', height: '100%', minWidth: '100vw', minHeight: '100vh', bgcolor: 'background.default' }}>
        <HeroSection>
          <Container maxWidth="lg" sx={{ zIndex: 2 }}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'rgba(241, 245, 249, 0.9)',
                    }}
                  >
                    Tahoe Basin • Development & Brokerage
                  </Typography>
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700, 
                      textShadow: '0 18px 40px rgba(0,0,0,0.65)',
                      maxWidth: 520,
                      fontSize: {
                        xs: '2.5rem',
                        sm: '3rem',
                        md: '3.5rem',
                        lg: '4rem',
                      },
                    }}
                  >
                    Your Project.
                    <br />
                    Your Vision.
                    <br />
                    Our Expertise.
                  </Typography>
                  <Typography 
                    variant="h6" 
                    paragraph 
                    sx={{ 
                      textShadow: '0 10px 25px rgba(0,0,0,0.6)',
                      maxWidth: 520,
                      fontSize: {
                        xs: '1rem',
                        sm: '1.05rem',
                        md: '1.15rem',
                      },
                      mb: 4,
                    }}
                  >
                    Scott Development delivers thoughtful, sustainable resorts and communities in and around Lake Tahoe—from land acquisition through sales and operations.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => navigate('/projects')}
                    >
                      View Flagship Projects
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="large"
                      onClick={() => navigate('/contact')}
                      sx={{
                        borderColor: 'rgba(249, 250, 251, 0.6)',
                        color: 'rgba(249, 250, 251, 0.9)',
                        '&:hover': {
                          borderColor: 'rgba(249, 250, 251, 1)',
                          backgroundColor: 'rgba(15, 23, 42, 0.3)',
                        },
                      }}
                    >
                      Schedule a Conversation
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'flex-end',
                }}
              >
                <Box
                  sx={{
                    maxWidth: 320,
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    p: 3,
                    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.45)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Typography variant="subtitle2" color="primary" sx={{ letterSpacing: '0.18em', mb: 1 }}>
                    FEATURED
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    Incline Creek Estates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    58 smart homes designed for mountain living, minutes from Lake Tahoe.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    300K+ sq. ft. delivered · LEED-focused construction
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </HeroSection>

        <Box
          component="section"
          aria-labelledby="home-mission-heading"
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 0, sm: 0 },
            bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
            borderTop: theme => `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="md">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-64px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  display: 'block',
                  mb: 2,
                }}
              >
                Mission
              </Typography>
              <Typography
                id="home-mission-heading"
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  letterSpacing: '-0.03em',
                  fontSize: { xs: '1.65rem', sm: '2rem', md: '2.25rem' },
                  lineHeight: 1.25,
                  mb: 3,
                  maxWidth: 720,
                }}
              >
                Align land, capital, and long-term stewardship for owners who measure success in decades.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 640,
                  fontSize: { xs: '1rem', md: '1.0625rem' },
                  lineHeight: 1.75,
                  mb: 4,
                }}
              >
                Scott Development partners with family offices, investors, and project sponsors on resort and residence-club opportunities across the Western United States—anchored in the Tahoe Basin—including Qualified Opportunity Zone strategies where they fit the plan. We pair rigorous analysis with on-the-ground judgment so communities stay durable, financeable, and true to place.
              </Typography>
              <Box
                sx={{
                  pl: { xs: 2.5, md: 3 },
                  borderLeft: theme => `3px solid ${theme.palette.secondary.main}`,
                  py: 0.5,
                  mb: 4,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: 'italic',
                    color: 'text.primary',
                    fontWeight: 500,
                    lineHeight: 1.65,
                    maxWidth: 560,
                  }}
                >
                  Every engagement is led by people who have shepherded complex approvals and sales—not by software alone—while modern tools quietly sharpen how we model risk, schedule, and outcomes.
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                }}
              >
                Family offices · Qualified Opportunity Zones · Resort & residence clubs · Western U.S.
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container sx={{ py: 10 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <StatsBox>
                <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                  300K+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Square feet of commercial and mixed-use development delivered.
                </Typography>
              </StatsBox>
            </Grid>
            <Grid item xs={12} md={3}>
              <StatsBox>
                <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                  250+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Luxury multi- and single-family residences across the Tahoe region.
                </Typography>
              </StatsBox>
            </Grid>
            <Grid item xs={12} md={3}>
              <StatsBox>
                <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                  $200M+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capital deployed and managed on behalf of investors.
                </Typography>
              </StatsBox>
            </Grid>
            <Grid item xs={12} md={3}>
              <StatsBox>
                <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                  $3.8B+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gross closed sales volume in residential, resort, and commercial assets.
                </Typography>
              </StatsBox>
            </Grid>
          </Grid>
        </Container>

        <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
          <Container>
            <Grid container spacing={4} sx={{ mb: 4 }} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <Typography 
                  variant="overline"
                  sx={{
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  What we do
                </Typography>
                <Typography 
                  variant="h2" 
                  component="h2" 
                  gutterBottom 
                  color="primary.main"
                  sx={{
                    fontSize: {
                      xs: '1.9rem',
                      sm: '2.4rem',
                      md: '2.9rem',
                    },
                  }}
                >
                  Full-cycle development, brokerage, and advisory.
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ maxWidth: 640 }}
                >
                  From early entitlement strategy through sales and ongoing operations, we steward complex resort and residential projects with a single, aligned team.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/services')}
                >
                  View all services
                </Button>
              </Grid>
            </Grid>
            <Grid container spacing={4}>
              {services.map((service, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <ServiceCard elevation={3}>
                    <Typography variant="h5" gutterBottom>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {service.description}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 'auto' }}>
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <Typography component="li" key={idx} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </ServiceCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Footer />
      </Box>
  );
} 